import { quat, type Quat } from '../math/quat'
import type { Vec3 } from '../math/vec3'
import { clamp } from '../math/index'

/**
 * Driven damped pendulum for dangling jewellery (earrings, pendants).
 * Two swing angles around the camera-space X and Z axes; the pivot's own motion
 * (estimated from consecutive positions) drives the swing, gravity restores it.
 * Camera-space -Y is treated as gravity, which holds for hand-held/upright cameras.
 * Portable: pure math, fixed-substep semi-implicit Euler, no allocations in update().
 */
export interface PendulumParams {
  /** Effective pendulum length (cm) — pivot to centre of mass. */
  lengthCm: number
  /** Angular damping (1/s). Higher = swings die out faster. */
  damping: number
  /** Gravity (cm/s²). */
  gravityCmS2: number
  /** Swing clamp (rad) so extreme head motion can't spin the earring. */
  maxSwingRad: number
  /** Scale on pivot-acceleration drive (0 = ignore motion, 1 = physical). */
  response: number
}

export const PENDULUM_DEFAULTS: PendulumParams = {
  lengthCm: 2.5,
  damping: 4.0,
  gravityCmS2: 980,
  maxSwingRad: 1.1,
  response: 1.0,
}

const MAX_SUBSTEP = 1 / 120

export class PendulumSim {
  readonly params: PendulumParams
  /** Swing about camera Z (lateral, +ve tips the bob toward +X). */
  private thZ = 0
  /** Swing about camera X (front/back, +ve tips the bob toward -Z). */
  private thX = 0
  private wZ = 0
  private wX = 0
  private prevPivot: Vec3 = [0, 0, 0]
  private prevVel: Vec3 = [0, 0, 0]
  private lastT: number | null = null

  constructor(params: Partial<PendulumParams> = {}) {
    this.params = { ...PENDULUM_DEFAULTS, ...params }
  }

  /**
   * Advance the sim with the pivot's current camera-space position (cm).
   * @param tMs frame timestamp in ms
   */
  update(pivot: Vec3, tMs: number): void {
    const t = tMs / 1000
    if (this.lastT === null) {
      this.lastT = t
      this.prevPivot[0] = pivot[0]
      this.prevPivot[1] = pivot[1]
      this.prevPivot[2] = pivot[2]
      return
    }
    let dt = t - this.lastT
    this.lastT = t
    if (!(dt > 0)) return
    dt = clamp(dt, 1 / 240, 1 / 15)

    // pivot velocity and acceleration by finite differences
    const vx = (pivot[0] - this.prevPivot[0]) / dt
    const vy = (pivot[1] - this.prevPivot[1]) / dt
    const vz = (pivot[2] - this.prevPivot[2]) / dt
    const ax = (vx - this.prevVel[0]) / dt
    const az = (vz - this.prevVel[2]) / dt
    void vy
    this.prevPivot[0] = pivot[0]
    this.prevPivot[1] = pivot[1]
    this.prevPivot[2] = pivot[2]
    this.prevVel[0] = vx
    this.prevVel[1] = vy
    this.prevVel[2] = vz

    const { lengthCm: L, damping: c, gravityCmS2: g, maxSwingRad, response } = this.params
    const steps = Math.min(8, Math.max(1, Math.ceil(dt / MAX_SUBSTEP)))
    const h = dt / steps
    for (let i = 0; i < steps; i++) {
      // lateral: pivot accel +X swings bob toward -X (inertia)
      const aZdd = -(g / L) * Math.sin(this.thZ) - c * this.wZ - (response * ax / L) * Math.cos(this.thZ)
      // front/back: pivot accel +Z swings bob toward -Z
      const aXdd = -(g / L) * Math.sin(this.thX) - c * this.wX + (response * az / L) * Math.cos(this.thX)
      this.wZ += aZdd * h
      this.wX += aXdd * h
      this.thZ = clamp(this.thZ + this.wZ * h, -maxSwingRad, maxSwingRad)
      this.thX = clamp(this.thX + this.wX * h, -maxSwingRad, maxSwingRad)
    }
  }

  /** Camera-space rotation of the hanging model (identity = hanging straight down). */
  getSwing(out: Quat): Quat {
    // rotation about +Z by thZ maps the hanging bob (0,-1,0) to (sin thZ, -cos thZ, 0): +thZ tips toward +X
    return quat.fromEuler(out, this.thX, 0, this.thZ)
  }

  /** Current swing magnitude (rad) — for tests / LOD. */
  get swingAngle(): number {
    return Math.hypot(this.thX, this.thZ)
  }
  get angularSpeed(): number {
    return Math.hypot(this.wX, this.wZ)
  }

  reset(): void {
    this.thX = this.thZ = this.wX = this.wZ = 0
    this.lastT = null
  }
}
